<template>
  <div class="menu-management">
    <div class="page-header">
      <h1>小程序菜单管理</h1>
      <div class="header-actions">
        <el-button type="primary" @click="handleAdd">
          <el-icon><Plus /></el-icon>
          添加菜单项
        </el-button>
        <el-button @click="loadMenus">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 版型筛选 -->
    <div class="filter-section">
      <el-select v-model="filterBoardType" placeholder="筛选版型" clearable @change="loadMenus" style="width: 200px;">
        <el-option label="全部" value="" />
        <el-option 
          v-for="board in boardTypes" 
          :key="board.id" 
          :label="board.name" 
          :value="board.id" 
        />
      </el-select>
    </div>

    <!-- 小程序菜单列表 -->
    <el-table
      v-loading="loading"
      :data="menuConfigs"
      stripe
      style="width: 100%"
      empty-text="暂无菜单配置"
    >
      <el-table-column prop="board_type" label="版型" width="100">
        <template #default="{ row }">
          <el-tag v-if="row.board_type === '1'" type="primary">{{ getBoardTypeName('1') }}</el-tag>
          <el-tag v-else-if="row.board_type === '2'" type="success">{{ getBoardTypeName('2') }}</el-tag>
          <el-tag v-else-if="row.board_type === '3'" type="warning">{{ getBoardTypeName('3') }}</el-tag>
          <el-tag v-else>{{ getBoardTypeName(row.board_type) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="menu_item_name" label="菜单名称" min-width="150" />
      <el-table-column prop="menu_item_key" label="菜单标识" min-width="150" />
      <el-table-column prop="menu_item_icon" label="图标" width="120" />
      <el-table-column prop="menu_item_url" label="URL" min-width="200" show-overflow-tooltip />
      <el-table-column prop="sort_order" label="排序" width="80" />
      <el-table-column prop="is_enabled" label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.is_enabled ? 'success' : 'info'">
            {{ row.is_enabled ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button type="primary" link size="small" @click="handleEdit(row)">
            编辑
          </el-button>
          <el-button type="danger" link size="small" @click="handleDelete(row)">
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 编辑对话框 -->
    <el-dialog
      v-model="showEditDialog"
      :title="editMode ? '编辑菜单项' : '添加菜单项'"
      width="600px"
      @close="resetForm"
    >
      <el-form
        ref="menuFormRef"
        :model="menuForm"
        :rules="menuRules"
        label-width="120px"
      >
        <el-form-item label="版型" prop="board_type">
          <el-select v-model="menuForm.board_type" placeholder="请选择版型">
            <el-option 
              v-for="board in boardTypes" 
              :key="board.id" 
              :label="board.name" 
              :value="board.id" 
            />
          </el-select>
        </el-form-item>
        <el-form-item label="菜单标识" prop="menu_item_key">
          <el-select
            v-model="menuForm.menu_item_key"
            placeholder="请选择或输入菜单标识"
            filterable
            allow-create
            default-first-option
          >
            <el-option label="配置角色" value="role" />
            <el-option label="设置课程表" value="schedule" />
            <el-option label="设置留言板" value="board" />
            <el-option label="熄屏设置" value="sleep" />
            <el-option label="设备图片管理" value="device_image_manager" />
          </el-select>
        </el-form-item>
        <el-form-item label="菜单名称" prop="menu_item_name">
          <el-input v-model="menuForm.menu_item_name" placeholder="请输入菜单名称" />
        </el-form-item>
        <el-form-item label="图标" prop="menu_item_icon">
          <el-input v-model="menuForm.menu_item_icon" placeholder="请输入图标名称（可选）" />
        </el-form-item>
        <el-form-item label="URL" prop="menu_item_url">
          <el-input v-model="menuForm.menu_item_url" placeholder="请输入跳转URL（可选）" />
        </el-form-item>
        <el-form-item label="排序" prop="sort_order">
          <el-input-number v-model="menuForm.sort_order" :min="0" :max="999" />
        </el-form-item>
        <el-form-item label="状态" prop="is_enabled">
          <el-switch v-model="menuForm.is_enabled" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Refresh } from '@element-plus/icons-vue';
import {
  getMenuConfigs,
  createMenuConfig,
  updateMenuConfig,
  deleteMenuConfig
} from '../api/menu';
import { getBoardTypes } from '../api/device';

export default {
  name: 'MenuManagement',
  components: {
    Plus,
    Refresh
  },
  setup() {
    const loading = ref(false);
    const saving = ref(false);
    const menuConfigs = ref([]);
    const filterBoardType = ref('');
    const showEditDialog = ref(false);
    const editMode = ref(false);
    const menuFormRef = ref(null);
    const boardTypes = ref([]);

    // 菜单项名称映射
    const menuItemNameMap = {
      'role': '配置角色',
      'schedule': '设置课程表',
      'board': '设置留言板',
      'sleep': '熄屏设置',
      'device_image_manager': '设备图片管理'
    };

    const menuForm = reactive({
      id: null,
      board_type: '',
      menu_item_key: '',
      menu_item_name: '',
      menu_item_icon: '',
      menu_item_url: '',
      sort_order: 0,
      is_enabled: 1
    });

    const menuRules = {
      board_type: [
        { required: true, message: '请选择版型', trigger: 'change' }
      ],
      menu_item_key: [
        { required: true, message: '请选择菜单项', trigger: 'change' }
      ],
      menu_item_name: [
        { required: true, message: '请输入菜单名称', trigger: 'blur' }
      ]
    };

    // 加载菜单列表
    const loadMenus = async () => {
      try {
        loading.value = true;
        const response = await getMenuConfigs(filterBoardType.value);
        if (response.success) {
          menuConfigs.value = response.data || [];
        } else {
          ElMessage.error(response.message || '加载菜单列表失败');
        }
      } catch (error) {
        console.error('加载菜单列表失败:', error);
        ElMessage.error('加载菜单列表失败');
      } finally {
        loading.value = false;
      }
    };

    // 添加菜单项
    const handleAdd = () => {
      editMode.value = false;
      resetForm();
      showEditDialog.value = true;
    };

    // 编辑菜单项
    const handleEdit = (row) => {
      editMode.value = true;
      Object.assign(menuForm, {
        id: row.id,
        board_type: row.board_type,
        menu_item_key: row.menu_item_key,
        menu_item_name: row.menu_item_name,
        menu_item_icon: row.menu_item_icon || '',
        menu_item_url: row.menu_item_url || '',
        sort_order: row.sort_order || 0,
        is_enabled: row.is_enabled
      });
      showEditDialog.value = true;
    };

    // 删除菜单项
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除"${row.menu_item_name}"吗？`,
          '提示',
          {
            type: 'warning'
          }
        );
        
        const response = await deleteMenuConfig(row.id);
        if (response.success) {
          ElMessage.success('删除成功');
          loadMenus();
        } else {
          ElMessage.error(response.message || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除菜单项失败:', error);
          ElMessage.error('删除菜单项失败');
        }
      }
    };

    // 保存菜单项
    const handleSave = async () => {
      try {
        await menuFormRef.value.validate();
        if (typeof menuForm.menu_item_key === 'string') {
          menuForm.menu_item_key = menuForm.menu_item_key.trim();
        }
        
        // 如果选择了菜单标识，自动填充菜单名称
        if (menuForm.menu_item_key && !menuForm.menu_item_name) {
          menuForm.menu_item_name = menuItemNameMap[menuForm.menu_item_key] || menuForm.menu_item_key;
        }
        
        saving.value = true;
        const response = editMode.value
          ? await updateMenuConfig(menuForm.id, menuForm)
          : await createMenuConfig(menuForm);
        
        if (response.success) {
          ElMessage.success(editMode.value ? '更新成功' : '创建成功');
          showEditDialog.value = false;
          loadMenus();
        } else {
          ElMessage.error(response.message || (editMode.value ? '更新失败' : '创建失败'));
        }
      } catch (error) {
        if (error !== false) {
          console.error('保存菜单项失败:', error);
          ElMessage.error('保存菜单项失败');
        }
      } finally {
        saving.value = false;
      }
    };

    // 重置表单
    const resetForm = () => {
      Object.assign(menuForm, {
        id: null,
        board_type: '',
        menu_item_key: '',
        menu_item_name: '',
        menu_item_icon: '',
        menu_item_url: '',
        sort_order: 0,
        is_enabled: 1
      });
      menuFormRef.value?.resetFields();
    };

    // 监听菜单标识变化，自动填充菜单名称
    watch(() => menuForm.menu_item_key, (newKey) => {
      const key = typeof newKey === 'string' ? newKey.trim() : newKey;
      if (key && menuItemNameMap[key]) menuForm.menu_item_name = menuItemNameMap[key];
    });

    const loadBoardTypes = async () => {
      try {
        const response = await getBoardTypes();
        if (response.success && response.data) {
          boardTypes.value = response.data;
        }
      } catch (error) {
        console.error('获取版型列表失败:', error);
      }
    };
    
    // 获取版型名称
    const getBoardTypeName = (typeId) => {
      if (!typeId) return '-';
      const board = boardTypes.value.find(b => String(b.id) === String(typeId));
      if (board) return board.name;
      
      // 不再提供降级方案，强制要求数据库迁移
      return typeId;
    };

    onMounted(() => {
      loadBoardTypes();
      loadMenus();
    });

    return {
      loading,
      saving,
      menuConfigs,
      filterBoardType,
      showEditDialog,
      editMode,
      menuFormRef,
      menuForm,
      menuRules,
      loadMenus,
      handleAdd,
      handleEdit,
      handleDelete,
      handleSave,
      resetForm,
      boardTypes,
      getBoardTypeName
    };
  }
};
</script>

<style scoped>
.menu-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.filter-section {
  margin-bottom: 20px;
}
</style>

